<template>
  <div class="faq-page">
    <!-- Hero Section -->
    <section class="page-hero">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content" data-aos="fade-up">
          <h1 class="page-title">{{ hero.title }}</h1>
          <p class="page-subtitle">{{ hero.subtitle }}</p>
        </div>
      </div>
    </section>

    <!-- Search Bar -->
    <section class="search-section">
      <div class="container">
        <div class="search-wrapper" data-aos="fade-up">
          <i class="fas fa-search"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search for answers..."
            class="search-input"
          >
        </div>
      </div>
    </section>

    <!-- FAQ Categories -->
    <section class="faq-content">
      <div class="container">
        <div class="row g-5">
          <!-- Sidebar Categories -->
          <div class="col-lg-4" data-aos="fade-right">
            <div class="categories-sidebar">
              <h3 class="sidebar-title">Categories</h3>
              <ul class="category-list">
                <li 
                  v-for="(category, index) in categories" 
                  :key="index"
                  :class="{ active: activeCategory === category.id }"
                  @click="activeCategory = category.id"
                >
                  <i :class="category.icon"></i>
                  <span>{{ category.name }}</span>
                  <span class="count">{{ category.questions.length }}</span>
                </li>
              </ul>
            </div>
          </div>
          
          <!-- FAQ Accordion -->
          <div class="col-lg-8" data-aos="fade-left">
            <div v-for="category in filteredCategories" :key="category.id" class="faq-category-section">
              <h2 class="category-title">
                <i :class="category.icon"></i>
                {{ category.name }}
              </h2>
              
              <div class="accordion-wrapper">
                <div 
                  v-for="(item, index) in category.questions" 
                  :key="index"
                  class="accordion-item"
                >
                  <div 
                    class="accordion-header" 
                    @click="toggleAccordion(category.id, index)"
                  >
                    <h4 class="question">{{ item.question }}</h4>
                    <i 
                      class="fas fa-chevron-down arrow" 
                      :class="{ rotated: isOpen(category.id, index) }"
                    ></i>
                  </div>
                  <div 
                    class="accordion-content" 
                    :class="{ active: isOpen(category.id, index) }"
                  >
                    <p class="answer">{{ item.answer }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Still Have Questions -->
    <section class="contact-cta">
      <div class="container">
        <div class="cta-content" data-aos="fade-up">
          <div class="cta-icon">
            <i class="fas fa-headset"></i>
          </div>
          <h2 class="cta-title">{{ cta.title }}</h2>
          <p class="cta-text">{{ cta.text }}</p>
          <div class="cta-buttons">
            <a href="/contact" class="btn-cta">
              <i class="fas fa-envelope"></i>
              Contact Us
            </a>
            <a :href="'tel:' + cta.phone" class="btn-cta-outline">
              <i class="fas fa-phone"></i> Call Us
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import CmsService from "../services/CmsService";
export default {
  name: 'FAQPage',
  data() {
    return {
      page: null,
      searchQuery: '',
      activeCategory: '',
      openItems: {},
    };
  },
  computed: {
    hero()       { return this.page?.sections?.hero       || {}; },
    cta()        { return this.page?.sections?.cta        || {}; },
    categories() { return this.page?.sections?.categories || []; },
    filteredCategories() {
      let cats = this.categories;
      if (this.activeCategory) cats = cats.filter(c => c.id === this.activeCategory);
      if (!this.searchQuery.trim()) return cats;
      const q = this.searchQuery.toLowerCase();
      return cats.map(cat => {
        const qs = cat.questions.filter(x => x.question.toLowerCase().includes(q) || x.answer.toLowerCase().includes(q));
        return qs.length ? { ...cat, questions: qs } : null;
      }).filter(Boolean);
    }
  },
  methods: {
    load() {
      CmsService.getPage('faq').then(r => {
        this.page = r.data.data;
        if (this.categories.length) this.activeCategory = this.categories[0].id;
      }).catch(() => {});
    },
    toggleAccordion(categoryId, index) {
      const key = `${categoryId}-${index}`;
      this.openItems = { ...this.openItems, [key]: !this.openItems[key] };
    },
    isOpen(categoryId, index) {
      return this.openItems[`${categoryId}-${index}`] || false;
    }
  },
  mounted() {
    this.load();
    if (typeof AOS !== 'undefined') AOS.init({ duration: 800, once: true, offset: 100 });
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Playfair+Display:wght@400;500;600;700&display=swap');

.faq-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ========== HERO SECTION ========== */
.page-hero {
  height: 300px;
  background: linear-gradient(135deg, rgba(118, 172, 175, 0.9), rgba(98, 152, 155, 0.9));
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 80px;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 2;
}

.page-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.page-subtitle {
  font-size: clamp(1rem, 2vw, 1.3rem);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
}

/* ========== SEARCH SECTION ========== */
.search-section {
  padding: 60px 0;
  background: white;
  margin-top: -80px;
  position: relative;
  z-index: 10;
}

.search-wrapper {
  max-width: 700px;
  margin: 0 auto;
  position: relative;
  background: white;
  border-radius: 50px;
  box-shadow: 0 15px 50px rgba(0,0,0,0.15);
  padding: 10px 25px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.search-wrapper i {
  color: rgb(118, 172, 175);
  font-size: 1.3rem;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.05rem;
  padding: 12px 0;
  font-family: 'Inter', sans-serif;
}

.search-input::placeholder {
  color: #a0aec0;
}

/* ========== FAQ CONTENT ========== */
.faq-content {
  /*padding: 80px 0 100px 0;*/
  background: white;
}

/* Sidebar */
.categories-sidebar {
  background: #f8fafb;
  padding: 30px;
  border-radius: 20px;
  position: sticky;
  top: 100px;
}

.sidebar-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 20px;
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.category-list li {
  padding: 15px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  color: #4a5568;
}

.category-list li:hover {
  background: rgba(118, 172, 175, 0.1);
  color: rgb(118, 172, 175);
}

.category-list li.active {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
}

.category-list li i {
  font-size: 1.1rem;
}

.category-list li span:first-of-type {
  flex: 1;
  font-weight: 500;
}

.category-list li .count {
  background: rgba(255,255,255,0.2);
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.category-list li.active .count {
  background: rgba(255,255,255,0.3);
}

/* FAQ Accordion */
.faq-category-section {
  margin-bottom: 50px;
}

.category-title {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.category-title i {
  color: rgb(118, 172, 175);
}

.accordion-wrapper {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.accordion-item {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s;
}

.accordion-item:hover {
  border-color: rgb(118, 172, 175);
  box-shadow: 0 4px 15px rgba(118, 172, 175, 0.1);
}

.accordion-header {
  padding: 25px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.question {
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  font-size: 1.1rem;
}

.arrow {
  color: rgb(118, 172, 175);
  transition: transform 0.3s;
  flex-shrink: 0;
}

.arrow.rotated {
  transform: rotate(180deg);
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
}

.accordion-content.active {
  max-height: 500px;
}

.answer {
  padding: 0 25px 25px 25px;
  color: #718096;
  line-height: 1.8;
  margin: 0;
  font-size: 1.02rem;
}

/* ========== CONTACT CTA ========== */
.contact-cta {
  padding: 100px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.cta-content {
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}

.cta-icon {
  width: 100px;
  height: 100px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.cta-icon i {
  font-size: 3rem;
  color: rgb(118, 172, 175);
}

.cta-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 20px;
}

.cta-text {
  font-size: 1.15rem;
  color: #4a5568;
  margin-bottom: 35px;
  line-height: 1.7;
}

.cta-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-cta,
.btn-cta-outline {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 30px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s;
  text-decoration: none;
}

.btn-cta {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
}

.btn-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(118, 172, 175, 0.4);
  color: white;
}

.btn-cta-outline {
  background: white;
  color: rgb(118, 172, 175);
  border: 2px solid rgb(118, 172, 175);
}

.btn-cta-outline:hover {
  background: rgb(118, 172, 175);
  color: white;
  transform: translateY(-3px);
}

/* ========== MOBILE RESPONSIVE ========== */
@media (max-width: 992px) {
  .categories-sidebar {
    position: static;
    margin-bottom: 40px;
  }
}

@media (max-width: 768px) {
  .page-hero {
    height: 300px;
    margin-top: 70px;
  }
  
  .search-section {
    padding: 40px 0;
    margin-top: -60px;
  }
  
  .faq-content,
  .contact-cta {
    padding: 60px 0;
  }
  
  .accordion-header {
    padding: 20px;
  }
  
  .answer {
    padding: 0 20px 20px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .btn-cta,
  .btn-cta-outline {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}
</style>